import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import * as styles from './SingleSignOnForms.module.scss';

import { CovidenceLogo } from './CovidenceLogo';
import {
  Button,
  Form,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Text,
} from 'components/core';
import { Stack } from 'components/layout';
import { getCsrfToken } from 'util/authenticityToken';
import {
  SIGN_IN_SAML_SIGNUP,
  SIGN_IN_SAML_LINK_EMAIL,
  FORGOT_PASSWORD,
} from 'query/routes';

type SamlEmailLinkPageProps = {
  useremail?: string;
  org: string;
  servererror?: string;
};

type FormValues = {
  session: {
    email: string;
    password: string;
  };
};

export const SamlEmailLinkPage = ({
  org,
  servererror,
  useremail,
}: SamlEmailLinkPageProps) => {
  const { register, handleSubmit, formState } = useForm<FormValues>({
    mode: 'onSubmit',
  });

  const submit: SubmitHandler<FormValues> = (_data, event) => {
    if (event) {
      const formElement = event.target as HTMLFormElement;
      formElement.submit(); // fallback for older submit logic
    }
  };

  return (
    <div className={`${styles.formContainer}`}>
      <div className={`${styles.wrapper}`}>
        <Form
          method="POST"
          action={SIGN_IN_SAML_LINK_EMAIL}
          className={styles.form}
          id="samlEmailLinkForm"
          onSubmit={handleSubmit(submit)}
        >
          <input
            name="authenticity_token"
            type="hidden"
            value={getCsrfToken()}
          />
          <Stack spacing={6}>
            <CovidenceLogo />
            <Heading type="h4" component="h2" className={styles.heading}>
              Already have an account?
            </Heading>
            <Text size="md" className={styles.infoText}>
              Sign in to connect your existing account with {org} SSO and keep
              all your work in one place
            </Text>
            <FormControl
              error={
                (formState.errors.session?.email?.message && 'Required') ||
                servererror
              }
            >
              <FormLabel>Email</FormLabel>
              <Input
                size="md"
                {...register('session.email', { required: 'Required' })}
                name="session[email]"
                defaultValue={useremail || ``}
              />
            </FormControl>
            <FormControl
              error={
                (formState.errors.session?.password?.message && 'Required') ||
                servererror
              }
            >
              <FormLabel>Password</FormLabel>
              <Input
                size="md"
                {...register('session.password', { required: 'Required' })}
                name="session[password]"
                inputType="password"
              />
              <Link to={FORGOT_PASSWORD} variant="primary">
                <Text size="sm">Forgot password?</Text>
              </Link>
            </FormControl>
            <Button
              size="md"
              className={styles.submitButton}
              type="brand"
              htmlType="submit"
            >
              Sign in
            </Button>
            <div className={styles.dividerWrapper}>
              <span className={styles.dividerLine}></span>
              <Text size="sm" className={styles.dividerText}>
                OR
              </Text>
              <span className={styles.dividerLine}></span>
            </div>
            <Button
              size="md"
              className={styles.submitButton}
              type="neutral"
              variant="outline"
              htmlType="button"
              onClick={() => {
                window.location.href = SIGN_IN_SAML_SIGNUP;
              }}
            >
              Create a new account
            </Button>
          </Stack>
        </Form>
      </div>
    </div>
  );
};
